import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

const ViewMoversForQuote = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const quote = state?.quote || null;
  const [selectedMovers, setSelectedMovers] = useState([]);
  const [adminMovers, setAdminMovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadStatus, setLeadStatus] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedMoverForReview, setSelectedMoverForReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [allMovers, setAllMovers] = useState([]);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [userPhone, setUserPhone] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Fetch mover details based on selected_mover_ids and assigned_mover_ids
  useEffect(() => {
    const fetchMovers = async () => {
      if (!quote || !quote.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch lead to check status and assigned movers
        const { data: leadData } = await supabase
          .from('leads')
          .select('status, selected_mover_ids, assigned_mover_ids')
          .eq('id', quote.id)
          .single();
        
        setLeadStatus(leadData?.status || 'Pending');

        // If lead is completed, only show the mover who was reviewed
        if (leadData?.status === 'Completed') {
          const { data: reviewData } = await supabase
            .from('mover_reviews')
            .select('mover_id')
            .eq('lead_id', quote.id)
            .single();

          if (reviewData?.mover_id) {
            const { data: completedMover } = await supabase
              .from('movers_data')
              .select('*')
              .eq('id', reviewData.mover_id)
              .single();

            if (completedMover) {
              setSelectedMovers([completedMover]);
              setAdminMovers([]);
              setAllMovers([completedMover]);
            }
          }
        } else {
          // For non-completed leads, show all selected and assigned movers
          // Get mover IDs from leadData or from quote object
          const selectedIds = leadData?.selected_mover_ids || quote?.selectedMoverIds || [];
          const assignedIds = leadData?.assigned_mover_ids || [];

          // Combine selected and assigned IDs for "Your Recommended Movers" section
          // These are movers either selected by user OR assigned to them
          const combinedIds = [...new Set([...selectedIds, ...assignedIds])];

          // Fetch all movers that are either selected or assigned
          if (combinedIds.length > 0) {
            const { data: moversData } = await supabase
              .from('movers_data')
              .select('*')
              .in('id', combinedIds);
            
            if (moversData) {
              // Separate movers into two categories:
              // 1. Movers selected by user (show in left section)
              const userSelected = moversData.filter(mover => selectedIds.includes(mover.id));
              
              // 2. Movers ONLY assigned by admin (not selected by user) - show in right section
              const adminOnly = moversData.filter(mover => 
                assignedIds.includes(mover.id) && !selectedIds.includes(mover.id)
              );
              
              setSelectedMovers(userSelected);
              setAdminMovers(adminOnly);
              setAllMovers(moversData); // Store all movers for review modal
            }
          }
        }

        // Check if user has already submitted a review for this lead
        const { data: reviewData } = await supabase
          .from('mover_reviews')
          .select('*')
          .eq('lead_id', quote.id)
          .single();

        if (reviewData) {
          setHasReviewed(true);
          setExistingReview(reviewData);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchMovers();
  }, [quote]);

  const isApproved = leadStatus === 'Assigned' || leadStatus === 'Contacted' || leadStatus === 'Completed';

  const handleSubmitReview = async () => {
    if (!selectedMoverForReview || !rating || !reviewText.trim()) {
      toast.error('Please select a mover, provide a rating, and write a review.');
      return;
    }

    if (!quote || !quote.id) {
      toast.error('Unable to submit review. Quote information is missing.');
      return;
    }

    // Check if review already exists for this lead
    if (hasReviewed) {
      toast.error('You have already submitted a review for this request.');
      return;
    }

    try {
      setSubmittingReview(true);
      
      // Use the signed-in user's information directly
      const reviewerName = user?.name || 'Customer';
      const reviewerPhone = user?.phone || 'N/A';
      
      
      // Submit review to database
      const { data, error } = await supabase
        .from('mover_reviews')
        .insert({
          mover_id: selectedMoverForReview.id,
          lead_id: quote.id,
          user_phone: reviewerPhone,
          rating: rating,
          review_text: reviewText,
          reviewer_name: reviewerName,
          status: 'approved',
          is_verified: true,
          is_approved: true
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          toast.error('You have already submitted a review for this request.');
          setHasReviewed(true);
          setShowReviewModal(false);
          return;
        }
        throw error;
      }

      // Update lead status to 'Completed'
      const { error: updateError } = await supabase
        .from('leads')
        .update({ status: 'Completed' })
        .eq('id', quote.id);
      
      if (!updateError) {
        setLeadStatus('Completed');
      }
      
      // Mark as reviewed
      setHasReviewed(true);
      setExistingReview(data);
      
      // Reset modal and show success popup
      setShowReviewModal(false);
      setSelectedMoverForReview(null);
      setRating(0);
      setReviewText('');
      setShowSuccessPopup(true);
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Show error if no quote
  if (!quote) {
    return (
      <div className="min-h-[calc(100vh-8rem)] bg-yellow-50/40 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Quote Not Found</h2>
              <p className="text-gray-600 mb-4">
                Unable to load quote information. Please return to the dashboard and try again.
              </p>
              <button
                onClick={() => navigate('/user')}
                className="px-6 py-2 bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-lg font-semibold hover:shadow-md transition-all"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const MoverCard = ({ mover, isAdminAssigned = false }) => {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900">{mover.company_name}</h4>
              {mover.is_verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
                  ✓ Verified
                </span>
              )}
            </div>
            
            {isApproved ? (
              <>
                <div className="mt-2 flex items-center gap-3 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <span className="text-yellow-500">★</span>
                    <span className="font-medium">{mover.rating || 'N/A'}</span>
                    {mover.total_reviews && (
                      <span className="text-gray-400">({mover.total_reviews?.toLocaleString()} reviews)</span>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  <div>📍 {mover.city}, {mover.state}</div>
                  {mover.years_in_business && (
                    <div className="mt-1">🏢 {mover.years_in_business} years in business</div>
                  )}
                  {mover.phone && (
                    <div className="mt-1">📞 {mover.phone}</div>
                  )}
                  {mover.email && (
                    <div className="mt-1">✉️ {mover.email}</div>
                  )}
                </div>
                {mover.company_description && (
                  <div className="mt-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <div className="font-medium text-gray-700 mb-1">About</div>
                    <div className="line-clamp-3">{mover.company_description}</div>
                  </div>
                )}
                {isAdminAssigned && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-blue-50 text-blue-700 border border-blue-200">
                      ✓ Assigned by admin
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-2 text-sm text-gray-500 italic">
                Mover details will be visible once your request is approved.
              </div>
            )}
          </div>
        </div>
        {isApproved && (
          <div className="mt-4 flex gap-2">
            <button 
              className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-lg text-sm font-semibold hover:shadow-md transition-all"
              onClick={() => navigate(`/movers/${mover.id}`)}
            >
              View Full Details
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-yellow-50/40 py-8">
      <Helmet>
        <title>Your Movers – MyMoveAdvisor</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/user')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
            >
              ← Back to Dashboard
            </button>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                {user?.name ? `${user.name}'s Moving Request` : 'Your Moving Request'}
                <span className="relative group inline-flex items-center">
                  <svg
                    className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap shadow-lg z-10 font-normal">
                    ID: {quote?.id}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                  </span>
                </span>
              </h1>
              <div className="text-sm text-gray-600">
                {quote?.from?.city && quote?.to?.city && (
                  <span>{quote.from.city} → {quote.to.city}</span>
                )}
                {quote?.date && <span className="ml-4">📅 {quote.date}</span>}
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Side - Your Recommended Movers */}
            <div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 px-5 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Your Recommended Movers</h2>
                      <p className="text-sm text-gray-600 mt-1">
                        Movers you selected during quote creation
                      </p>
                    </div>
                    {!isApproved && (
                      <div className="bg-gray-100 border border-black-300 px-3 py-1 rounded-full text-xs font-medium text-yellow-800">
                        Pending Approval
                      </div>
                    )}
                    {isApproved && (
                      <div className="bg-green-100 border border-green-300 px-3 py-1 rounded-full text-xs font-medium text-green-800">
                        ✓ Approved
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                      <p className="text-gray-600 mt-4">Loading movers...</p>
                    </div>
                  ) : selectedMovers && selectedMovers.length > 0 ? (
                    <div className="space-y-4">
                      {selectedMovers.map((mover) => (
                        <MoverCard key={mover.id} mover={mover} isAdminAssigned={false} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-4">📦</div>
                      <div className="text-gray-600">No movers selected</div>
                      <div className="text-sm text-gray-500 mt-2">
                        You didn't select any movers when creating this quote
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Side - System Assigned Movers & Complete Request Button */}
            <div className="space-y-6">
              {/* Complete Request Button */}
              {isApproved && allMovers.length > 0 && !hasReviewed && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Is Your Request Completed?</h3>
                      <p className="text-sm text-gray-600 mt-1">Help us improve by sharing your experience</p>
                    </div>
                    <button
                      onClick={() => setShowReviewModal(true)}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all shadow-md hover:shadow-lg"
                    >
                      Leave a Review
                    </button>
                  </div>
                </div>
              )}

              {/* Already Reviewed Message */}
              {hasReviewed && existingReview && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">✓</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">Review Submitted</h3>
                      <p className="text-sm text-gray-600 mb-2">
                        You reviewed {allMovers.find(m => m.id === existingReview.mover_id)?.company_name || 'this mover'}
                      </p>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-yellow-500">{'★'.repeat(existingReview.rating)}{'☆'.repeat(5 - existingReview.rating)}</span>
                        <span className="text-gray-500">•</span>
                        <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                          Published
                        </span>
                      </div>
                      {existingReview.mover_reply && (
                        <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                          <div className="text-xs font-semibold text-gray-700 mb-1">Mover Reply:</div>
                          <p className="text-sm text-gray-600">{existingReview.mover_reply}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* System Assigned Movers */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-bold text-gray-900">System Assigned Movers</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Additional top-rated movers assigned by our team
                  </p>
                </div>
                <div className="p-5">
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      <p className="text-gray-600 mt-4">Loading...</p>
                    </div>
                  ) : adminMovers && adminMovers.length > 0 ? (
                    <div className="space-y-4">
                      {adminMovers.map((mover) => (
                        <MoverCard key={mover.id} mover={mover} isAdminAssigned={true} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-4">⏳</div>
                      <div className="text-gray-600">No additional movers assigned</div>
                      <div className="text-sm text-gray-500 mt-2">
                        Our team may assign additional movers to your request
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Review Modal */}
        {showReviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Complete Your Request</h2>
                  <p className="text-sm text-gray-600 mt-1">Select the mover you worked with and share your experience</p>
                </div>
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setSelectedMoverForReview(null);
                    setRating(0);
                    setReviewText('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Step 1: Select Mover */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Which mover did you work with?</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {allMovers.map((mover) => (
                      <button
                        key={mover.id}
                        onClick={() => setSelectedMoverForReview(mover)}
                        className={`flex items-center gap-4 p-4 border-2 rounded-xl transition-all ${
                          selectedMoverForReview?.id === mover.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {/* Mover Image/Logo */}
                        {mover.company_logo ? (
                          <img
                            src={mover.company_logo}
                            alt={mover.company_name}
                            className="w-16 h-16 rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className="w-16 h-16 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-semibold text-lg flex-shrink-0"
                          style={{ display: mover.company_logo ? 'none' : 'flex' }}
                        >
                          {mover.company_name?.charAt(0).toUpperCase() || '?'}
                        </div>

                        {/* Mover Info */}
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-gray-900">{mover.company_name}</div>
                          <div className="text-sm text-gray-600">
                            {mover.city}, {mover.state}
                          </div>
                        </div>

                        {/* Selected Indicator */}
                        {selectedMoverForReview?.id === mover.id && (
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 2: Rating */}
                {selectedMoverForReview && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">2. How would you rate your experience?</h3>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          className="group"
                        >
                          <svg
                            className={`w-12 h-12 transition-all ${
                              star <= rating
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300 hover:text-yellow-200'
                            }`}
                            fill={star <= rating ? 'currentColor' : 'none'}
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                            />
                          </svg>
                        </button>
                      ))}
                      {rating > 0 && (
                        <span className="ml-3 text-lg font-semibold text-gray-700">
                          {rating} {rating === 1 ? 'Star' : 'Stars'}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Review Text */}
                {selectedMoverForReview && rating > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Share your experience</h3>
                    <textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Tell us about your moving experience. What went well? Any suggestions?"
                      className="w-full h-32 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all resize-none"
                      maxLength={500}
                    />
                    <div className="text-sm text-gray-500 mt-1 text-right">
                      {reviewText.length}/500 characters
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setSelectedMoverForReview(null);
                    setRating(0);
                    setReviewText('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitReview}
                  disabled={!selectedMoverForReview || !rating || !reviewText.trim() || submittingReview}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Popup Modal */}
        {showSuccessPopup && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center transform animate-[scale-in_0.3s_ease-out]">
              {/* Success Icon */}
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Success Message */}
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Thank You!
              </h3>
              <p className="text-gray-600 mb-2">
                Your review has been submitted successfully.
              </p>
              <p className="text-gray-600 mb-6">
                Your request has been marked as <span className="font-semibold text-green-600">Completed</span>.
              </p>

              {/* Close Button */}
              <button
                onClick={() => setShowSuccessPopup(false)}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewMoversForQuote;
